import { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  Typography,
  Paper,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Edit as EditIcon } from '@mui/icons-material';
 
import StepModify from './otherComponent/StepModify';
import { ISESSION } from 'types/auth';
import CommonNodeSetting from './otherComponent/CommonNodeSetting/CommonNodeSetting';

type StatusItem = {
  _id: string;
  name: string;
  uid: string;
};

type NotificationPayload = {
  conditions: string[];
  users: string[];
  roles: string[];
  channels: string[];
};

type VideoCallFormProps = {
  selectedNodeData: ISESSION;
  onSubmit: (data: ISESSION) => void;
  onClose: () => void;
  selectedNode: any;
};

const VideoCallForm = ({ selectedNodeData, onSubmit, onClose, selectedNode }: VideoCallFormProps) => {
  const [initialValues, setInitialValues] = useState<ISESSION | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [notificationPayload, setNotificationPayload] = useState<NotificationPayload>({
    conditions: [],
    users: [],
    roles: [],
    channels: []
  });
  const [escalationPayload, setEscalationPayload] = useState({
    videoCall: { enabled: false, actors: [], users: [], callType: 'VIDEO' },
    chat: { enabled: false, actors: [], users: [] }
  });

  // Memoize the callback to prevent re-renders
  const handleStatusesLoaded = useCallback((list: StatusItem[]) => {
    console.log('📋 Statuses loaded:', list.length);
    setStatuses(list);
  }, []);

  // Memoize notification change handler to prevent re-renders
  const handleNotificationChange = useCallback((payload: NotificationPayload) => {
    console.log('🔔 Notification changed:', payload);
    setNotificationPayload(payload);
  }, []);

  // Memoize escalation change handler to prevent re-renders
  const handleEscalationChange = useCallback((payload: any) => {
    console.log('📞 VideoCallForm: Escalation changed:', payload);
    setEscalationPayload(payload);
  }, []); 

  // Memoize step type change handler
  const handleStepTypeChange = useCallback((newType: string, setFieldValue: any, submitForm: any) => {
    console.log('🔄 Step type changing to:', newType);
    const type = newType.toUpperCase();
    setFieldValue('stepType', type);
    setFieldValue('componentType', type);
    setFieldValue('data.title', type);
    setTimeout(() => {
      submitForm();
    });
  }, []);

  useEffect(() => {
    if (!selectedNodeData) return;

    const {
      componentType,
      name = selectedNodeData.component?.name || '',
      description,
      minimumAnswers,
      formType,
      settings,
      component = {},
      onComplete
    } = selectedNodeData;

    const actor = selectedNode?.data?.step?.actor?.map((a: any) => a?.uid) ?? [];

    // Extract and map notification data from backend
    const backendNotification = selectedNode?.data?.step?.notification || {};
    const mappedNotification = {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
        channel === 'IN-APP' ? 'In-App' : 
        channel === 'EMAIL' ? 'Email' :
        channel === 'SMS' ? 'SMS' : channel
      ) : []
    };

    setInitialValues({
      componentType,
      name,
      description,
      settings: {
        cameraMode: settings?.cameraMode ?? undefined,
        callType: settings?.callType ?? undefined
      },
      minimumAnswers,
      formType,
      component,
      data: {
        title: selectedNode?.data?.label || 'Video Call'
      },
      actor: actor,
      stepType: selectedNode?.step?.stepType || 'SESSION',
      onCompleteStatusUid: selectedNode?.data?.step?.onComplete?.status?.uid || '',
      onComplete: onComplete || {}
    });

    // Set notification payload separately to avoid type issues
    setNotificationPayload({
      conditions: [
        ...(mappedNotification.scheduleOnETA ? ['ON_ETA'] : []),
        ...(mappedNotification.onCompletion ? ['ON_COMPLETE'] : [])
      ],
      users: mappedNotification.notifyUsers || [],
      roles: mappedNotification.notifyRoles || [],
      channels: mappedNotification.notificationChannel && mappedNotification.notificationChannel.length > 0 
        ? mappedNotification.notificationChannel.map((channel: string) => 
            channel === 'In-App' ? 'IN-APP' : 
            channel === 'Email' ? 'EMAIL' :
            channel === 'SMS' ? 'SMS' : channel.toUpperCase()
          )
        : []
    });

    // Initialize escalation payload from existing node data
    setEscalationPayload(selectedNode?.data?.step?.escalation || {});
      // Pass the stored object straight through. Each form used to rebuild
      // this by hand from a fixed list of keys, which silently dropped any
      // field it did not know about — including `inherit`, so opening and
      // saving any step would reset it to the process default.
  }, [selectedNodeData.componentType, selectedNodeData.name, selectedNodeData.description, selectedNodeData.settings, selectedNode?.data?.label, selectedNode?.data?.step?.actor, selectedNode?.data?.step?.notification, selectedNode?.data?.step?.escalation, selectedNode?.data?.step?.onComplete?.status?.uid]);

  // Memoize notification initial values to prevent Notification component re-renders
  const notificationInitialValues = useMemo(() => {
    // Extract notification data from backend
    const backendNotification = selectedNode?.data?.step?.notification || {};
    return {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
        channel === 'IN-APP' ? 'In-App' : 
        channel === 'EMAIL' ? 'Email' :
        channel === 'SMS' ? 'SMS' : channel
      ) : []
    };
  }, [selectedNode?.data?.step?.notification]);

   

  return initialValues ? (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        const selectedStatus = statuses.find(s => s.uid === values.onCompleteStatusUid);

        const updatedData: ISESSION = {
          ...values,
          component: {
            ...values.component,
            name: values.name,
            componentType: values.componentType || 'SESSION'
          },
          onComplete: selectedStatus ? { status: selectedStatus } : {}
        };

        // Add notification and escalation to the payload (even if not in ISESSION type)
        const finalData = {
          ...updatedData,
          notification: notificationPayload,
          escalation: escalationPayload
        };

        onSubmit(finalData as ISESSION);
        setSubmitting(false);
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, submitForm }) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Paper sx={{ p: 2, width: '100%' }} elevation={0}>
            <Box component="form" onSubmit={handleSubmit}>
              <Box display="flex" justifyContent="center" alignItems="center" mb={3} borderBottom={1} borderColor="grey.300" pb={2}>
                {isEditingTitle ? (
                  <Box display="flex" alignItems="center" sx={{ width: '100%', maxWidth: '600px' }}>
                    <TextField 
                      name="data.title" 
                      value={values.data.title} 
                      onChange={handleChange} 
                      autoFocus 
                      fullWidth 
                      sx={{ mr: 2 }}
                    />
                    <Button variant="contained" size="small" onClick={() => setIsEditingTitle(false)}>
                      Save
                    </Button>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
                    <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
                      <Typography 
                        variant="h5" 
                        fontWeight={500}
                        sx={{
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={values.data.title}
                      >
                        {values.data.title}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => setIsEditingTitle(true)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {selectedNode && (
                      <Box sx={{ minWidth: '120px', minHeight: '36px' }}>
                        <StepModify
                          selectedNode={selectedNode}
                          onStepTypeChange={(newType) => handleStepTypeChange(newType, setFieldValue, submitForm)}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: -1 }}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    multiline
                    minRows={3}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    size="small"
                  />
                </Grid>
              </Grid>

               
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography fontWeight='bold'>Session Configuration</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box borderColor="grey.300">
                    <Grid container spacing={2}>
                      <Grid item xs={6}><Typography>Camera Mode:</Typography></Grid>
                      <Grid item xs={6}>
                        <Select
                          name="settings.cameraMode"
                          value={values.settings?.cameraMode || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                        >
                          <MenuItem value="environment">Environment</MenuItem>
                          <MenuItem value="front">Front</MenuItem>
                        </Select>
                      </Grid>

                      <Grid item xs={6}><Typography>Call Type:</Typography></Grid>
                      <Grid item xs={6}>
                        <Select
                          name="settings.callType"
                          value={values.settings?.callType || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                        >
                          <MenuItem value="audio">Audio</MenuItem>
                          <MenuItem value="video">Video</MenuItem>
                        </Select>
                      </Grid>
                    </Grid>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <CommonNodeSetting
                  selectedNode={selectedNode}
                  onStatusesLoaded={handleStatusesLoaded}
                  onNotificationChange={handleNotificationChange}
                  // onInstructionChange={handleInstructionChange}
                  notificationInitialValues={notificationInitialValues}
                  // instructionInitialValues={instructionInitialValues}
                  escalationPayload={escalationPayload}
                  onEscalationChange={handleEscalationChange}
                />
              </Grid>

              <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                <Button type="button" color="error" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  Close
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Formik>
  ) : null;
};

export default memo(VideoCallForm);
